import { cn } from '@moment/design-system-ui/lib/cn'
import { cva, type VariantProps } from 'class-variance-authority'

// Define the text component variants - dark theme only
const textVariants = cva('font-[family-name:var(--font-sans)] w-fit', {
  variants: {
    variant: {
      // 12px variants
      mono: 'text-[length:var(--text-xs)] leading-(--leading-xs) tracking-(--tracking-xs-dark) font-[family-name:var(--font-mono)] uppercase',

      // 13px variants
      sm: 'text-[length:var(--text-sm)] leading-(--leading-sm) tracking-(--tracking-sm-dark) font-[number:var(--font-weight-light)]',

      // 14px variants
      base: 'text-[length:var(--text-base)] leading-(--leading-base) tracking-(--tracking-base-dark) font-[number:var(--font-weight-light)]',
      'base-semibold':
        'text-[length:var(--text-base)] leading-(--leading-base) tracking-(--tracking-base-semibold-dark) font-[number:var(--font-weight-semibold)]',

      // 16px variants
      md: 'text-[length:var(--text-md)] leading-(--leading-md) tracking-(--tracking-md-dark) font-[number:var(--font-weight-light)]',
      'md-semibold':
        'text-[length:var(--text-md)] leading-(--leading-md) tracking-(--tracking-md-semibold-dark) font-[number:var(--font-weight-semibold)]',

      // 18px variants
      lg: 'text-[length:var(--text-lg)] leading-(--leading-lg) tracking-(--tracking-lg-dark) font-[number:var(--font-weight-light)]',

      // 28px variants
      xl: 'text-[length:var(--text-xl)] leading-(--leading-xl) tracking-(--tracking-xl-dark)',

      // 36px variants
      '2xl':
        'text-[length:var(--text-2xl)] leading-(--leading-2xl) tracking-(--tracking-2xl-dark)',

      // 42px variants
      '3xl':
        'text-[length:var(--text-3xl)] leading-(--leading-3xl) tracking-(--tracking-3xl-dark)',

      // 52px variants
      '4xl':
        'text-[length:var(--text-4xl)] leading-(--leading-4xl) tracking-(--tracking-4xl-dark)',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
})

type TextVariantProps = VariantProps<typeof textVariants>

export interface BaseTextProps extends TextVariantProps {
  balance?: boolean
  pretty?: boolean
  trim?: boolean
  loading?: boolean
}

// Get typography classes based on provided props
export function getTextClasses({
  variant = 'base',
  balance = false,
  pretty = false,
  className = '',
}: BaseTextProps & { className?: string }): string {
  const textWrapClasses: string[] = []
  if (balance) {
    textWrapClasses.push('text-balance')
  }
  if (pretty) {
    textWrapClasses.push('text-pretty')
  }

  return cn(
    textVariants({
      variant,
    }),
    ...textWrapClasses,
    className,
  )
}

// Get skeleton styles for loading state
export function getSkeletonClasses(
  variant = 'default',
  className = '',
): string {
  const skeletonBase =
    'animate-pulse bg-(--color-gray-200) dark:bg-(--color-gray-700) rounded'
  const variantClass = variant === 'default' ? 'w-full' : 'w-[70%]'

  return cn(skeletonBase, variantClass, className)
}

// Get skeleton height based on text variant
export function getSkeletonHeight(variant = 'base'): string {
  const variantHeights: Record<string, string> = {
    mono: '1rem',
    sm: '1.125rem',
    base: '1.25rem',
    'base-semibold': '1.25rem',
    md: '1.5rem',
    'md-semibold': '1.5rem',
    lg: '1.75rem',
    xl: '2.25rem',
    '2xl': '2.75rem',
    '3xl': '3.25rem',
    '4xl': '3.75rem',
  }

  return variantHeights[variant] || '1.25rem'
}

// Determine skeleton variant based on element type
export function getSkeletonVariant(variant: string, elementType = 'p'): string {
  if (
    variant.includes('xl') ||
    variant.includes('2xl') ||
    variant.includes('3xl') ||
    variant.includes('4xl') ||
    variant.includes('heading')
  ) {
    return 'heading'
  }

  if (elementType.startsWith('h')) {
    return elementType
  }

  return 'default'
}

// Get text trim styles for browsers that support it
export function getTextTrimStyles(trim: boolean): Record<string, string> {
  return trim
    ? {
        'text-box-trim': 'trim-both',
      }
    : {}
}
